import React from 'react';
import { Select } from 'antd';
import { Colors } from '@common/tokens';
import { Checkbox, Icon, Form, Table } from '@common/components';
import { selectInputFilterHelper } from '@common/services';

type CertificationType = {
  id: string;
  name: string;
  credential?: string;
  required?: boolean;
};

export function CertificationsSelect({
  certifications,
  selectedCertifications,
  setSelectedCertifications,
  hidden = false,
}: {
  certifications: CertificationType[];
  selectedCertifications: CertificationType[];
  setSelectedCertifications: (certifications: CertificationType[]) => void;
  hidden?: boolean;
}) {
  const selectCertification = (value: string): void => {
    let selectedCert = certifications.find(
      (certification) => certification.id === value
    );

    if (!selectedCert) {
      return;
    }

    selectedCert = { ...selectedCert, required: false };
    let newSelectedCertifications = [...selectedCertifications, selectedCert];
    selectedCertifications.forEach((selectedCert) => {
      if (selectedCert.id === value) {
        newSelectedCertifications = [...selectedCertifications];
      }
    });
    setSelectedCertifications(newSelectedCertifications);
  };

  const updateCertificationRequired = (id: string): void => {
    const cert = selectedCertifications.find((cert) => cert.id === id);

    if (!cert) {
      return;
    }

    cert.required = !cert.required;
    setSelectedCertifications([
      ...selectedCertifications.filter((cert) => cert.id !== id),
      cert,
    ]);
  };

  const removeCertification = (id: string): void => {
    setSelectedCertifications(
      selectedCertifications.filter((cert) => cert.id !== id)
    );
  };

  const selectedCertificationsColumns = [
    {
      title: 'Required',
      dataIndex: 'required',
      width: 60,
      render: (value: boolean, record: CertificationType) => (
        <Checkbox
          checked={value}
          onClick={() => updateCertificationRequired(record.id)}
        />
      ),
    },
    {
      title: 'Certification Name',
      dataIndex: 'name',
    },
    {
      title: 'Delete',
      width: 50,
      render: (record: CertificationType) => (
        <a onClick={() => removeCertification(record.id)}>
          <Icon kind="solid" icon="xMark" color="steelDarker" />
        </a>
      ),
    },
  ];

  return (
    <div className="CertificationsSelect">
      <Form.Item
        colon={false}
        hidden={hidden}
        label="Certifications"
        name="certifications"
      >
        <Select
          size="large"
          showSearch
          filterOption={selectInputFilterHelper}
          onSelect={selectCertification}
          placeholder="What certifications should this role have?"
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {certifications.map(({ id, name, credential }) => {
            return (
              <Select.Option key={id} value={id}>
                {`${credential} - ${name}`}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      {selectedCertifications.length > 0 && !hidden && (
        <Table
          dataSource={[...selectedCertifications].sort((a, b) =>
            a.name.localeCompare(b.name)
          )}
          columns={selectedCertificationsColumns}
          rowKey="id"
          pagination={false}
          bordered
          rowClassName="CertificationsTableRow"
        />
      )}
      <style jsx>{`
        .CertificationsSelect :global(.CertificationsTableRow) {
          background-color: ${Colors.steelLightest};
        }
      `}</style>
    </div>
  );
}
